import React, { useCallback, useEffect, useState } from "react";
import { ReferenceArea, Label } from "recharts";
import { Target, Layers, TrendingUp, TrendingDown, Zap, Play, BarChart3 } from "lucide-react";
import { getMarketZones, backtestZones } from "../api";

// Zone color scheme by state
const ZONE_COLORS = {
  supply: { fill: "rgba(239, 68, 68, 0.12)", stroke: "rgba(239, 68, 68, 0.5)" },
  demand: { fill: "rgba(34, 197, 94, 0.12)", stroke: "rgba(34, 197, 94, 0.5)" },
  turncoat: { fill: "rgba(168, 85, 247, 0.15)", stroke: "rgba(168, 85, 247, 0.55)" },
  breaker: { fill: "rgba(249, 115, 22, 0.15)", stroke: "rgba(249, 115, 22, 0.55)" },
  reclaimed: { fill: "rgba(6, 182, 212, 0.15)", stroke: "rgba(6, 182, 212, 0.55)" },
};

function getZoneColorKey(zone) {
  if (zone.reclaimed) return "reclaimed";
  if (zone.breaker) return "breaker";
  if (zone.turncoat) return "turncoat";
  return zone.is_supply ? "supply" : "demand";
}

function gradeLabel(zone) {
  const state = zone.reclaimed ? "R" : zone.breaker ? "B" : zone.turncoat ? "T" : "";
  return `${zone.grade}${state} ${zone.score}`;
}

function fmtPrice(value) {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs >= 10) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

/**
 * ZoneChartOverlay — renders zone ReferenceAreas on a Recharts chart.
 *
 * Props:
 *   zones      — array of zone dicts from /zones endpoint
 *   lastClose  — last candle close price
 *   atr        — ATR(14) value
 *   chartEnd   — timestamp for the right edge of the chart (x2 for zones)
 *   visible    — boolean to toggle overlay
 */
export function ZoneChartOverlay({ zones, lastClose, atr, chartEnd, visible = true }) {
  if (!visible || !zones?.length || !chartEnd) return null;

  return (
    <>
      {zones.map((zone, i) => {
        const colorKey = getZoneColorKey(zone);
        const colors = ZONE_COLORS[colorKey];
        const x1 = (zone.start_time || 0) * 1000; // zone start_time is unix seconds, chart uses ms
        const x2 = chartEnd;
        const label = gradeLabel(zone);

        return (
          <ReferenceArea
            key={`zone-${i}`}
            x1={x1}
            x2={x2}
            y1={zone.bot}
            y2={zone.top}
            fill={colors.fill}
            stroke={colors.stroke}
            strokeWidth={1}
            strokeDasharray={zone.touches > 2 ? "4 2" : "0"}
            ifOverflow="extendDomain"
          >
            <Label
              position="insideTopLeft"
              content={({ viewBox }) => {
                if (!viewBox) return null;
                const x = viewBox.x + 4;
                const y = viewBox.y + 12;
                const textColor = zone.is_supply ? "#ef4444" : "#22c55e";
                return (
                  <g>
                    <text
                      x={x}
                      y={y}
                      fontSize={9}
                      fontWeight="bold"
                      fill={textColor}
                      opacity={0.8}
                    >
                      {label}
                    </text>
                  </g>
                );
              }}
            />
          </ReferenceArea>
        );
      })}
    </>
  );
}

/**
 * ZoneInfoPanel — shows nearest zones with distance in ATR.
 */
export function ZoneInfoPanel({ zones, lastClose, atr, loading, error }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-slate-400 dark:text-slate-500">
        <Layers size={14} className="animate-pulse" /> Loading zones…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-red-500 dark:text-red-400">
        <Target size={14} /> {error}
      </div>
    );
  }
  if (!zones?.length) {
    return (
      <p className="py-3 text-xs text-slate-400 dark:text-slate-500">
        No zones detected on this timeframe.
      </p>
    );
  }

  // Sort by distance: nearest supply above, nearest demand below
  const supply = zones
    .filter(z => z.is_supply && !z.reclaimed && !z.breaker)
    .sort((a, b) => (a.distance_atr ?? 99) - (b.distance_atr ?? 99))
    .slice(0, 3);

  const demand = zones
    .filter(z => !z.is_supply && !z.reclaimed && !z.breaker)
    .sort((a, b) => (a.distance_atr ?? 99) - (b.distance_atr ?? 99))
    .slice(0, 3);

  const active = zones.filter(z => z.in_zone);

  return (
    <div className="space-y-3">
      {/* Current zone indicator */}
      {active.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 dark:bg-sky-950/30">
          <Zap size={14} className="text-sky-500" />
          <span className="text-xs font-semibold text-sky-700 dark:text-sky-300">
            Price in {active.length} zone{active.length > 1 ? "s" : ""}
          </span>
          <span className="text-[10px] text-sky-500 dark:text-sky-400">
            {active.map(z => `${z.is_supply ? "S" : "D"}${z.grade}${z.score}`).join(", ")}
          </span>
        </div>
      )}

      {/* Nearest supply */}
      {supply.length > 0 && (
        <div>
          <div className="mb-1.5 flex items-center gap-1.5">
            <TrendingDown size={12} className="text-red-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
              Nearest supply
            </span>
          </div>
          <div className="space-y-1">
            {supply.map((z, i) => (
              <div
                key={`s-${i}`}
                className="flex items-center justify-between rounded-lg bg-red-50/60 px-2.5 py-1.5 text-xs dark:bg-red-950/20"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-red-700 dark:text-red-300">
                    {fmtPrice(z.top)}
                  </span>
                  <span className="text-[10px] text-red-500 dark:text-red-400">
                    {z.grade} · {z.score}pts · {z.touches}t
                  </span>
                </div>
                <span className="font-mono text-[10px] font-semibold text-red-600 dark:text-red-400">
                  {z.distance_atr != null ? `${z.distance_atr.toFixed(1)} ATR` : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nearest demand */}
      {demand.length > 0 && (
        <div>
          <div className="mb-1.5 flex items-center gap-1.5">
            <TrendingUp size={12} className="text-emerald-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              Nearest demand
            </span>
          </div>
          <div className="space-y-1">
            {demand.map((z, i) => (
              <div
                key={`d-${i}`}
                className="flex items-center justify-between rounded-lg bg-emerald-50/60 px-2.5 py-1.5 text-xs dark:bg-emerald-950/20"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300">
                    {fmtPrice(z.bot)}
                  </span>
                  <span className="text-[10px] text-emerald-500 dark:text-emerald-400">
                    {z.grade} · {z.score}pts · {z.touches}t
                  </span>
                </div>
                <span className="font-mono text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                  {z.distance_atr != null ? `${z.distance_atr.toFixed(1)} ATR` : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ATR reference */}
      {atr && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500">
          ATR(14): {fmtPrice(atr)} · {zones.length} zone{zones.length !== 1 ? "s" : ""} total
        </p>
      )}
    </div>
  );
}

/**
 * useZones — hook to fetch zone data for a symbol/interval.
 */
export function useZones(symbol, interval, enabled = true) {
  const [zones, setZones] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetch = useCallback(() => {
    if (!symbol || !enabled) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getMarketZones(symbol, interval, 400, 0, controller.signal)
      .then(r => {
        const data = r.data;
        if (data.status === "ok") {
          setZones(data);
        } else {
          setZones(null);
          if (data.status !== "candle_data_unavailable") {
            setError(data.status?.replace(/_/g, " ") || "Zone data unavailable");
          }
        }
      })
      .catch(err => {
        if (err?.name !== "CanceledError") setError("Could not load zones");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [symbol, interval, enabled]);

  useEffect(() => {
    const cleanup = fetch();
    return cleanup;
  }, [fetch]);

  return { zones: zones?.zones || [], zoneData: zones, loading, error, refetch: fetch };
}

/**
 * ZoneBacktestPanel — run backtest on historical candles and display zone performance.
 */
export function ZoneBacktestPanel({ symbol, interval }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ran, setRan] = useState(false);

  const run = useCallback(() => {
    if (!symbol) return;
    setLoading(true);
    setError("");
    backtestZones(symbol, interval, 500, 50)
      .then(r => {
        const data = r.data;
        if (data.error === "no_candles" || data.error === "insufficient_candles") {
          setError("Not enough historical candles for a backtest.");
          setResult(null);
        } else {
          setResult(data);
        }
      })
      .catch(() => setError("Backtest could not run. Try again."))
      .finally(() => { setLoading(false); setRan(true); });
  }, [symbol, interval]);

  const metrics = result?.metrics || {};
  const byGrade = result?.by_grade || {};
  const byType = result?.by_type || {};

  return (
    <div className="space-y-4">
      {/* Run button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-violet-500" />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Zone backtest</span>
          {result && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {result.total_zones} zones · {result.total_candles} bars
            </span>
          )}
        </div>
        <button
          onClick={run}
          disabled={loading || !symbol}
          className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
        >
          <Play size={12} className={loading ? "animate-pulse" : ""} />
          {loading ? "Running…" : ran ? "Re-run" : "Run backtest"}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
      )}

      {!ran && !loading && !error && (
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Replay historical candles to measure how well zones were respected. Shows respect rate, average reaction, and breakdown by grade.
        </p>
      )}

      {result && (
        <div className="space-y-4">
          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-violet-50 p-3 dark:bg-violet-950/30">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-500 dark:text-violet-400">Respect rate</p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-violet-900 dark:text-violet-100">
                {metrics.respect_rate != null ? `${Math.round(metrics.respect_rate * 100)}%` : "—"}
              </p>
              <p className="text-[10px] text-violet-500 dark:text-violet-400">
                {metrics.respected || 0}/{metrics.touched || 0} touched zones held
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Avg reaction</p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {metrics.avg_reaction_atr != null ? `${metrics.avg_reaction_atr.toFixed(2)} ATR` : "—"}
              </p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500">
                max {metrics.max_reaction_atr?.toFixed(2) || "—"} ATR
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Avg lifespan</p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {metrics.avg_lifespan_bars != null ? `${Math.round(metrics.avg_lifespan_bars)} bars` : "—"}
              </p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500">
                {metrics.broken || 0} broken / {metrics.total || 0} total
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">ATR reference</p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {result.atr14 != null ? fmtPrice(result.atr14) : "—"}
              </p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500">
                via {result.candle_provider || "candle data"}
              </p>
            </div>
          </div>

          {/* By grade */}
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">By grade</p>
            <div className="grid grid-cols-3 gap-2">
              {["A", "B", "C"].map(grade => {
                const g = byGrade[grade] || {};
                const rate = g.respect_rate != null ? Math.round(g.respect_rate * 100) : null;
                const color = grade === "A" ? "text-emerald-600 dark:text-emerald-400"
                  : grade === "B" ? "text-sky-600 dark:text-sky-400"
                  : "text-slate-500 dark:text-slate-400";
                return (
                  <div key={grade} className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/60">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${color}`}>{grade}</span>
                      <span className={`font-mono text-sm font-bold tabular-nums ${color}`}>
                        {rate != null ? `${rate}%` : "—"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                      {g.touched || 0} touched · {g.total || 0} zones
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By type */}
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Supply vs demand</p>
            <div className="grid grid-cols-2 gap-2">
              {["supply", "demand"].map(type => {
                const t = byType[type] || {};
                const rate = t.respect_rate != null ? Math.round(t.respect_rate * 100) : null;
                const isSupply = type === "supply";
                const color = isSupply ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400";
                return (
                  <div key={type} className={`rounded-lg px-3 py-2 ${isSupply ? "bg-red-50/60 dark:bg-red-950/20" : "bg-emerald-50/60 dark:bg-emerald-950/20"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${color}`}>{isSupply ? "Supply" : "Demand"}</span>
                      <span className={`font-mono text-sm font-bold tabular-nums ${color}`}>
                        {rate != null ? `${rate}%` : "—"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                      {t.touched || 0} touched · avg {t.avg_reaction_atr?.toFixed(2) || "—"} ATR
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
